import { useCallback, useEffect, useRef, useState } from 'react';
import { mensagemDoErro } from './api';

interface EstadoRecurso<T> {
  dados: T | null;
  erro: string | null;
  carregando: boolean;
}

/**
 * Carrega um recurso da API e expõe `recarregar`.
 *
 * `chaves` controla quando refazer a busca — a função em si pode mudar de
 * identidade a cada render sem disparar requisição em laço.
 */
export function useRecurso<T>(carregar: () => Promise<T>, chaves: readonly unknown[] = []) {
  const [estado, setEstado] = useState<EstadoRecurso<T>>({
    dados: null,
    erro: null,
    carregando: true,
  });
  const referencia = useRef(carregar);
  referencia.current = carregar;
  const montado = useRef(true);

  const recarregar = useCallback(async () => {
    setEstado((atual) => ({ ...atual, carregando: true, erro: null }));
    try {
      const dados = await referencia.current();
      if (montado.current) setEstado({ dados, erro: null, carregando: false });
    } catch (falha) {
      if (montado.current) {
        setEstado((atual) => ({ ...atual, erro: mensagemDoErro(falha), carregando: false }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, chaves);

  useEffect(() => {
    montado.current = true;
    void recarregar();
    return () => {
      montado.current = false;
    };
  }, [recarregar]);

  return { ...estado, recarregar, definir: setEstado };
}
